import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, FileText, CheckCircle2, Clock, AlertTriangle, Sparkles, Eye, RefreshCw, Upload } from 'lucide-react';
import { matApi } from '../../api/materialthequeApi';
import { HudButton, HudBadge } from '../hud';

/**
 * MaterialthequeStatusCard — bandeau "état Matériauthèque" affiché dans la page projet.
 *
 * Affiche l'état du dernier job lié au projet :
 *   - aucun job   → invitation à lancer une analyse
 *   - en cours    → bandeau bleu + lien "Voir la progression"
 *   - terminé non validé → bandeau orange + "Valider l'extraction"
 *   - approuvé    → bandeau vert + "Voir la matériauthèque" + nom du fichier
 *   - erreur      → bandeau rouge + "Relancer"
 */
const MaterialthequeStatusCard = ({ projectId, refreshKey }) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    matApi
      .getProjectLatestJob(projectId)
      .then((r) => { if (!cancelled) setJob(r?.job || null); })
      .catch(() => { if (!cancelled) setJob(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, refreshKey]);

  if (loading) {
    return (
      <div
        className="rounded-xl px-4 py-3"
        style={{ background: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}
        data-testid="materialtheque-status-loading"
      >
        <span className="hud-eyebrow" style={{ color: 'var(--c-text-faint)' }}>
          Matériauthèque IA — chargement…
        </span>
      </div>
    );
  }

  const goToMaterialtheque = () => navigate(`/materialtheque?project=${projectId}`);

  // ---------- État 1 : aucun job ----------
  if (!job) {
    return (
      <div
        className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        style={{ background: 'var(--c-surface-1)', border: '1px dashed var(--c-border)' }}
        data-testid="materialtheque-status-empty"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
          >
            <Boxes className="w-4 h-4" style={{ color: 'var(--c-text-muted)' }} />
          </div>
          <div className="min-w-0">
            <span className="hud-eyebrow" style={{ color: 'var(--c-text-muted)' }}>Matériauthèque IA</span>
            <p className="text-[13px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
              Aucune analyse de CCTP pour ce projet.
            </p>
          </div>
        </div>
        <HudButton variant="secondary" size="sm" onClick={goToMaterialtheque} leftIcon={<Upload className="w-3.5 h-3.5" />} data-testid="materialtheque-status-launch">
          Analyser un CCTP
        </HudButton>
      </div>
    );
  }

  const fileName = job.file_name || 'CCTP';
  const total = job.materials_count || 0;
  const approved = !!job.approved_at;
  const isRunning = ['queued', 'parsing', 'extracting', 'thumbs'].includes(job.status);
  const isError = job.status === 'error';
  const isDone = job.status === 'done' && !approved;

  // ---------- État 2 : en cours ----------
  if (isRunning) {
    return (
      <div
        className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.02) 100%)',
          border: '1px solid rgba(59,130,246,0.3)',
        }}
        data-testid="materialtheque-status-running"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'rgba(59,130,246,0.18)', border: '1px solid rgba(59,130,246,0.35)' }}
          >
            <Sparkles className="w-4 h-4 animate-pulse" style={{ color: '#93C5FD' }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="hud-eyebrow" style={{ color: '#93C5FD' }}>Matériauthèque IA</span>
              <HudBadge tone="neutral" icon={Clock}>Analyse en cours</HudBadge>
            </div>
            <p className="text-[13px] mt-0.5 truncate" style={{ color: 'var(--c-text)' }}>
              <FileText className="inline w-3 h-3 mr-1" /> {fileName}
            </p>
          </div>
        </div>
        <HudButton variant="ghost" size="sm" onClick={goToMaterialtheque} leftIcon={<Eye className="w-3.5 h-3.5" />} data-testid="materialtheque-status-view-progress">
          Voir la progression
        </HudButton>
      </div>
    );
  }

  // ---------- État 3 : erreur ----------
  if (isError) {
    return (
      <div
        className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          background: 'linear-gradient(135deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.02) 100%)',
          border: '1px solid rgba(239,68,68,0.3)',
        }}
        data-testid="materialtheque-status-error"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.35)' }}
          >
            <AlertTriangle className="w-4 h-4" style={{ color: '#FCA5A5' }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="hud-eyebrow" style={{ color: '#FCA5A5' }}>Matériauthèque IA</span>
              <HudBadge tone="warning">Échec de l'extraction</HudBadge>
            </div>
            <p className="text-[12.5px] mt-0.5 truncate" style={{ color: 'var(--c-text-muted)' }}>
              {job.error || 'Erreur durant l\'analyse'} · {fileName}
            </p>
          </div>
        </div>
        <HudButton variant="secondary" size="sm" onClick={goToMaterialtheque} leftIcon={<RefreshCw className="w-3.5 h-3.5" />} data-testid="materialtheque-status-relaunch">
          Relancer
        </HudButton>
      </div>
    );
  }

  // ---------- État 4 : terminé non validé ----------
  if (isDone) {
    return (
      <div
        className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          background: 'linear-gradient(135deg, rgba(217,160,91,0.12) 0%, rgba(217,160,91,0.03) 100%)',
          border: '1px solid var(--c-border-accent)',
        }}
        data-testid="materialtheque-status-pending"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'var(--c-gold-soft)', border: '1px solid var(--c-border-accent)' }}
          >
            <Sparkles className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="hud-eyebrow hud-eyebrow-gold">Matériauthèque IA</span>
              <HudBadge tone="gold">À valider</HudBadge>
            </div>
            <p className="text-[13px] mt-0.5 truncate" style={{ color: 'var(--c-text)' }}>
              {total} matériaux extraits · <FileText className="inline w-3 h-3 mx-1" />{fileName}
            </p>
          </div>
        </div>
        <HudButton variant="primary" size="sm" onClick={goToMaterialtheque} leftIcon={<Eye className="w-3.5 h-3.5" />} data-testid="materialtheque-status-validate">
          Vérifier &amp; valider
        </HudButton>
      </div>
    );
  }

  // ---------- État 5 : approuvé ----------
  return (
    <div
      className="rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
      style={{
        background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)',
        border: '1px solid rgba(34,197,94,0.3)',
      }}
      data-testid="materialtheque-status-approved"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.35)' }}
        >
          <CheckCircle2 className="w-4 h-4" style={{ color: '#86EFAC' }} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="hud-eyebrow" style={{ color: '#86EFAC' }}>Matériauthèque IA</span>
            <HudBadge tone="success" icon={CheckCircle2}>Validée</HudBadge>
          </div>
          <p className="text-[13px] mt-0.5 truncate" style={{ color: 'var(--c-text)' }}>
            {total} matériaux validés · <FileText className="inline w-3 h-3 mx-1" />{fileName}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <HudButton variant="ghost" size="sm" onClick={goToMaterialtheque} leftIcon={<Eye className="w-3.5 h-3.5" />} data-testid="materialtheque-status-view">
          Voir
        </HudButton>
        <HudButton variant="secondary" size="sm" onClick={goToMaterialtheque} leftIcon={<RefreshCw className="w-3.5 h-3.5" />} data-testid="materialtheque-status-relaunch-approved">
          Nouvelle analyse
        </HudButton>
      </div>
    </div>
  );
};

export default MaterialthequeStatusCard;
